<template>
  <el-select
    v-close-on-zoom
    :value="selected"
    :placeholder="placeholder"
    :filterable="true"
    :default-first-option="true"
    class="medication-select"
    :disabled="disabled"
    :multiple="multiple"
    @input="id => selectItem(id)"
  >
    <el-option
      v-for="medItem in availableMedItems"
      :key="medItem.id"
      :label="isGlobal ? medicationLabel(medItem) : dosageFormLabel(medItem)"
      :value="isGlobal ? medItem.id : medItem.medicationId"
    />
    <el-option
      v-for="fluid in availableFluids"
      :key="fluid.key"
      :label="fluidLabel(fluid.key)"
      :value="fluid.key"
    />
    <!-- In case the selected medications is not in the list of available medications
            we still want to display it so the user sees the name of the medication instead of an id -->
    <el-option
      v-if="
        selectedMedItem && !isMedItemAvailable(selectedMedItem.id)
      " :key="selectedMedItem.id"
      :label="'*' + medicationLabel(selectedMedItem)"
      :value="selectedMedItem.id"
    />
    <!-- In case the selected fluid is not in the list of available fluids
            we still want to display it so the user sees the name of the fluid instead of an id -->
    <el-option
      v-if="
        selectedFluid && !availableFluids.find(f => f.key === selectedFluid.key)
      " :key="selectedFluid.key"
      :label="'*' + selectedFluid.label.en"
      :value="selectedFluid.key"
    />
  </el-select>
</template>

<script>
import keyBy from 'lodash/keyBy';
import { fluids } from '~lib/common/config/fluids';
import medicationLabel from '~lib/common/utils/medicationLabel';

export default {
  name: 'medication-fluid-selector',
  components: {},
  props: {
    medications: { type: Array, default: () => [] },
    caseConf: { type: Object, required: true, },
    selected: { type: [String, Array], default: null },
    disabled: { type: Boolean },
    dosageFormFilter: { type: Function, default: null },
    multiple: { type: Boolean, default: false, },
    showMeds: { type: Boolean, default: true, },
    showFluids: { type: Boolean, default: true, },
    isGlobal: { type: Boolean, default: false },
  },
  data() {
    return {};
  },
  computed: {
    allMedications() {
      return (this.medications || []).concat(this.caseConf.customMedications || []);
    },
    allMedicationsById() {
      return keyBy(this.allMedications, 'id');
    },
    allFluids() {
      return fluids.concat(this.caseConf.customFluids || []);
    },
    allFluidsByKey() {
      return keyBy(this.allFluids, 'key');
    },
    availableMedItems() {
      if (!this.showMeds) {
        return [];
      }
      else if (this.isGlobal) {
        return this.medications;
      } else {
        return this.availableDosageForms;
      }
    },
    availableDosageForms() {
      const { dosageForms } = this.caseConf;
      if (this.dosageFormFilter) {
        return (dosageForms || []).filter(this.dosageFormFilter);
      }
      return dosageForms || [];
    },
    availableFluids() {
      if (!this.showFluids) {
        return [];
      }
      if (this.isGlobal) {
        return fluids;
      }
      return this.caseConf.fluids || [];
    },
    selectedMedItem() {
      if (this.multiple) {
        return null;
      }
      if (!this.selected) {
        return null;
      }
      return this.allMedicationsById[this.selected];
    },
    selectedFluid() {
      if (this.multiple) {
        return null;
      }
      if (!this.selected) {
        return null;
      }
      return this.allFluidsByKey[this.selected];
    },
    placeholder() {
      if (this.showMeds && !this.showFluids) {
        return 'Medication';
      } else if (!this.showMeds && this.showFluids) {
        return 'Fluid'
      }
        return 'Medication or Fluid';
    }
  },
  methods: {
    selectItem(id) {
      if (this.availableDosageForms.find(df => df.medicationId === id)) {
        this.selectMedication(id)
      } else {
        this.selectFluid(id)
      }
    },
    isMedItemAvailable(id) {
      if (this.isGlobal) {
        return true;
      } else {
        return !!this.availableDosageForms.find(df => df.medicationId === id);
      }
    },
    selectMedication(medicationId) {
      this.$emit('select', 'MEDICATION', medicationId);
    },
    selectFluid(fluidId) {
      this.$emit('select', 'FLUID', fluidId);
    },
    medicationLabel(medication) {
      return medicationLabel(
        medication.dosageForm,
        medication.group,
        medication.defaultConcentration,
        { includeGroup: true },
      );
    },
    dosageFormLabel(dosageForm) {
      const medication = this.allMedicationsById[dosageForm.medicationId];
      if (!medication) {
        return 'N/A';
      }
      return medicationLabel(
        dosageForm.dosageFormType,
        medication.group,
        dosageForm.concentration,
        {
          includeGroup: true,
          customGroups: this.caseConf.customMedGroups || []
        },
      );
    },
    fluidLabel(fluidId) {
      const fluid = this.allFluids.find(fluid => fluid.key === fluidId);
      if (!fluid) {
        return 'N/A';
      }
      return fluid.abbreviation.en;
    }
  },
};
</script>

<style scoped>
.medication-select {
  flex: 0.9;
}
</style>
